import { useState } from 'react';
import type { JSX } from 'react';
import { Button, Modal, useNavigationPort } from '@taki/ui';
import { useAppRuntime } from '@taki/app-runtime';
import type { ProprietesDeFenetreDeFiche } from '@taki/app-runtime';
import { raisonDe } from './dossier/morceaux.js';
import { useAnnuaire } from './dossier/useAnnuaire.js';
import { NouvelleVisite } from './NouvelleVisite.js';
import type { VisiteDemandee } from './NouvelleVisite.js';
import { SANS_CONSULTATIONS, consultationDeLaVisite } from './visite.js';

/**
 * « CONSULTER MAINTENANT », la fenetre d'app du geste declare `consulter-maintenant`
 * (`niveau: "fiche"` sur la porte `animaux` de `taki.screens.json`, fenetre
 * `consulterMaintenant` de `taki.app.ts`). Le socle rend le geste dans la rangee
 * de la fiche, a gauche apres la sortie, sur la Fiche standard ET sur le Dossier
 * de l'animal : une declaration, un seul code, jamais deux copies qui divergent.
 *
 * Le geste : choisir le motif (et le veterinaire), creer la consultation « En
 * consultation » par le SDK, puis l'ouvrir dans l'app Consultations par le port
 * de navigation du shell. Sans cette app (pas de destination), la fenetre le dit
 * et ne propose que de fermer : le socle grise le geste en lecture seule, mais
 * cette raison-la est metier, elle se dit ici (G16).
 */
export function FenetreConsulterMaintenant({ fiche, fermer }: ProprietesDeFenetreDeFiche): JSX.Element {
  const navigation = useNavigationPort();
  const destination = navigation?.destination('careRecords', 'consultation') ?? null;
  if (navigation === null || destination === null) {
    return (
      <Modal
        open
        onClose={fermer}
        title="Consulter maintenant"
        footer={
          <Button variant="secondary" onClick={fermer}>
            Fermer
          </Button>
        }
      >
        <p className="text-sm text-ink-900">{SANS_CONSULTATIONS}</p>
      </Modal>
    );
  }
  const ouvrir = (id: string): void => navigation.ouvrir(destination, id, String(fiche['name'] ?? ''));
  return <VisiteMaintenant animalId={String(fiche['id'])} fermer={fermer} ouvrir={ouvrir} />;
}

function VisiteMaintenant({
  animalId,
  fermer,
  ouvrir,
}: {
  animalId: string;
  fermer: () => void;
  ouvrir: (consultationId: string) => void;
}): JSX.Element {
  const { sdk, orgId = '' } = useAppRuntime();
  const { moi, personnes } = useAnnuaire();
  const [erreur, setErreur] = useState<string | null>(null);

  const creer = async (demande: VisiteDemandee): Promise<boolean> => {
    try {
      const creee = await sdk.careRecords.create(consultationDeLaVisite(orgId, animalId, demande));
      fermer();
      ouvrir(creee.id);
      return true;
    } catch (cause) {
      setErreur(`Visite non créée : ${raisonDe(cause)}`);
      return false;
    }
  };

  return (
    <NouvelleVisite mode="maintenant" personnes={personnes} veterinaireParDefaut={moi} erreur={erreur} onCreer={creer} onAnnuler={fermer} />
  );
}

export default FenetreConsulterMaintenant;
